import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateManualTimeEntryInput,
  ImportTimeEntriesInput,
  TimeEntryImportAnalysis,
  TimeEntryImportResult,
  CreateTtCategoryInput,
  CreateTtProjectInput,
  CreateTtSubCategoryInput,
  ItemResponse,
  ListResponse,
  MyYearOverview,
  MyYearOverviewQuery,
  StartTimerInput,
  StopTimerInput,
  TimeTrackingAnalytics,
  TimeTrackingAnalyticsEmployee,
  TimeTrackingAnalyticsQuery,
  TtAllEntriesPage,
  TtAllEntriesQuery,
  TtAllEntryRow,
  CreateTimerFavoriteEntryInput,
  ReorderTimerFavoriteEntriesInput,
  TimerCompany,
  TimerFavorite,
  TimerFavoriteEntry,
  TimerProject,
  TimerRecentPreset,
  UpdateTimerFavoriteEntryInput,
  TimeTrackingDashboard,
  TimeTrackingEntryResponse,
  TtCategoryResponse,
  TtProjectResponse,
  TtSubCategoryResponse,
  UpdateTimeTrackingEntryInput,
  UpdateTtCategoryInput,
  UpdateTtProjectInput,
  UpdateTtSubCategoryInput,
} from '@proerp/shared';
import { apiRequest } from '@/lib/apiClient';

const TT_KEY = ['time-tracking'] as const;

export interface MyEntriesFilters {
  page?: number;
  pageSize?: number;
  q?: string;
  dateFrom?: string;
  dateTo?: string;
  legalEntityId?: string;
}

/**
 * Invalidiert alles Zeiterfassungs-Bezogene (Timer, Dashboard, Protokoll) — und
 * den Schnellstart: „Zuletzt verwendet" und die Favoriten-Zähler entstehen aus
 * den eigenen Buchungen, hängen aber unter `['tt-master']` (`QUICK_KEY`). Ohne
 * das blieben die Kacheln nach Start/Stopp stehen (Live-Review 2026-09-28).
 */
function useInvalidateTt() {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: TT_KEY }),
      queryClient.invalidateQueries({ queryKey: QUICK_KEY }),
    ]);
}

/**
 * Der laufende Timer. `enabled` hängt in der Kopfzeile am Recht
 * `timetracking.read` — ohne das soll niemand im Minutentakt eine Abfrage
 * auslösen, die er ohnehin nicht sehen darf.
 */
export function useRunningTimer(enabled = true) {
  return useQuery({
    queryKey: [...TT_KEY, 'running'],
    queryFn: () => apiRequest<ItemResponse<TimeTrackingEntryResponse | null>>('/v1/time-tracking/running'),
    // Häufiger nachladen, damit die Timer-Pille nach Start/Stopp aktuell ist.
    refetchInterval: 60_000,
    enabled,
  });
}

/*
 * Start und Stopp laden auch nach einem FEHLER neu (`onSettled`): Scheitern sie,
 * weil ein anderer Tab schon gestartet/gestoppt hat („Es läuft aktuell kein
 * Timer."), ist genau der Cache veraltet — sonst bliebe die Pille grün, obwohl
 * nichts mehr läuft (Live-Review 2026-09-28).
 */
export function useStartTimer() {
  const invalidate = useInvalidateTt();
  return useMutation({
    mutationFn: (input: StartTimerInput) =>
      apiRequest<ItemResponse<TimeTrackingEntryResponse>>('/v1/time-tracking/start', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSettled: invalidate,
  });
}

export function useStopTimer() {
  const invalidate = useInvalidateTt();
  return useMutation({
    mutationFn: (input: StopTimerInput = {}) =>
      apiRequest<ItemResponse<TimeTrackingEntryResponse>>('/v1/time-tracking/stop', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSettled: invalidate,
  });
}

export function useTtDashboard() {
  return useQuery({
    queryKey: [...TT_KEY, 'dashboard'],
    queryFn: () => apiRequest<ItemResponse<TimeTrackingDashboard>>('/v1/time-tracking/dashboard'),
  });
}

/**
 * Admin-Analyse (Recht `timetracking.manage`): tenant-weite Auswertung über alle
 * Mitarbeiter. Ohne Zeitraum liefert der Server den laufenden Kalendermonat.
 */
/**
 * Persönliche Jahresübersicht (Recht `timetracking.read`) — nur die eigenen
 * Buchungen; der Mitarbeiter wird serverseitig aus dem angemeldeten User
 * aufgelöst (kein employeeId-Parameter, anders als bei der Admin-Analyse).
 */
export function useMyYearOverview(query: MyYearOverviewQuery) {
  const params = new URLSearchParams();
  if (query.year !== undefined) params.set('year', String(query.year));
  const qs = params.toString();
  return useQuery({
    queryKey: [...TT_KEY, 'my-year-overview', query],
    queryFn: () =>
      apiRequest<ItemResponse<MyYearOverview>>(`/v1/time-tracking/my-year-overview${qs ? `?${qs}` : ''}`),
  });
}

export function useTtAnalytics(query: TimeTrackingAnalyticsQuery) {
  // Alle gesetzten Felder statt einer festen Liste (wie `useTtAllEntries`) —
  // die feste Liste ließ `allTime` und das Projekt still fallen (2026-09-24).
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return useQuery({
    queryKey: [...TT_KEY, 'analytics', query],
    queryFn: () =>
      apiRequest<ItemResponse<TimeTrackingAnalytics>>(`/v1/time-tracking/analytics${qs ? `?${qs}` : ''}`),
  });
}

/**
 * Einzelbuchungen der Admin-Analyse (Recht `timetracking.manage`), seitenweise.
 * `keepPreviousData` bewusst NICHT gesetzt: beim Filterwechsel soll die Tabelle
 * nicht kurz veraltete Zeilen zeigen, die zum neuen Filter nicht passen.
 */
export function useTtAllEntries(query: TtAllEntriesQuery) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined && value !== '') params.set(key, String(value));
  }
  const qs = params.toString();
  return useQuery({
    queryKey: [...TT_KEY, 'analytics-entries', query],
    queryFn: () =>
      apiRequest<ItemResponse<TtAllEntriesPage>>(
        `/v1/time-tracking/analytics-entries${qs ? `?${qs}` : ''}`,
      ),
  });
}

/**
 * Lädt ALLE Buchungen des aktuellen Filters für den CSV-Export (nicht nur die
 * angezeigte Seite) — `DataTable.csv.fetchAll`. Ohne das exportierte der Knopf
 * still 50 von 3000 Zeilen, und das fällt erst in Excel auf.
 *
 * In Seiten von 500 (Server-Obergrenze) geholt, mit Deckel bei 20 000 Zeilen:
 * darüber ist eine CSV kein sinnvolles Werkzeug mehr, und der Aufrufer sagt es
 * dem Nutzer, statt stillschweigend abzuschneiden.
 */
export const TT_ALL_ENTRIES_EXPORT_LIMIT = 20_000;

export async function fetchAllTtEntries(
  query: Omit<TtAllEntriesQuery, 'page' | 'pageSize'>,
): Promise<{ rows: TtAllEntryRow[]; isTruncated: boolean }> {
  const rows: TtAllEntryRow[] = [];
  const pageSize = 500;
  for (let page = 1; ; page += 1) {
    const params = new URLSearchParams();
    for (const [key, value] of Object.entries({ ...query, page, pageSize })) {
      if (value !== undefined && value !== '') params.set(key, String(value));
    }
    const response = await apiRequest<ItemResponse<TtAllEntriesPage>>(
      `/v1/time-tracking/analytics-entries?${params.toString()}`,
    );
    rows.push(...response.data.items);
    const isLastPage = rows.length >= response.data.total || response.data.items.length === 0;
    if (isLastPage) return { rows, isTruncated: false };
    if (rows.length >= TT_ALL_ENTRIES_EXPORT_LIMIT) return { rows, isTruncated: true };
  }
}

/** Mitarbeiter mit Zeitbuchungen — für den Mitarbeiter-Filter der Zeit-Analyse. */
export function useTtAnalyticsEmployees() {
  return useQuery({
    queryKey: [...TT_KEY, 'analytics-employees'],
    queryFn: () => apiRequest<ListResponse<TimeTrackingAnalyticsEmployee>>('/v1/time-tracking/analytics-employees'),
  });
}

/** Projekte (Zeit- UND ProERP-Projekte) mit Zeitbuchungen — für den Projekt-Filter der Admin-Analyse. */
export function useTtAnalyticsProjects() {
  return useQuery({
    queryKey: [...TT_KEY, 'analytics-projects'],
    queryFn: () => apiRequest<ListResponse<TimerProject>>('/v1/time-tracking/analytics-projects'),
  });
}

export function useMyTimeEntries(filters: MyEntriesFilters = {}) {
  const params = new URLSearchParams();
  params.set('page', String(filters.page ?? 1));
  params.set('pageSize', String(filters.pageSize ?? 100));
  if (filters.q) params.set('q', filters.q);
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.legalEntityId) params.set('legalEntityId', filters.legalEntityId);
  return useQuery({
    queryKey: [...TT_KEY, 'entries', filters],
    queryFn: () =>
      apiRequest<ListResponse<TimeTrackingEntryResponse>>(`/v1/time-tracking/entries?${params.toString()}`),
  });
}

/**
 * Alle eigenen Einträge EINES Kalendertags (JJJJ-MM-TT, Berlin) — für Tage an der
 * Seitengrenze des Zeitprotokolls, damit Tagessumme und Pause-Knopf mit dem ganzen
 * Tag rechnen statt mit dem Teil auf der aktuellen Seite. `day` leer = nichts laden.
 */
export function useMyDayEntries(
  day: string | undefined,
  filters: Pick<MyEntriesFilters, 'q' | 'legalEntityId'> = {},
) {
  return useQuery({
    queryKey: [...TT_KEY, 'entries', 'day', day ?? null, filters],
    queryFn: () => {
      const params = new URLSearchParams({ page: '1', pageSize: '500', dateFrom: day!, dateTo: day! });
      if (filters.q) params.set('q', filters.q);
      if (filters.legalEntityId) params.set('legalEntityId', filters.legalEntityId);
      return apiRequest<ListResponse<TimeTrackingEntryResponse>>(`/v1/time-tracking/entries?${params.toString()}`);
    },
    enabled: Boolean(day),
  });
}

/**
 * Lädt ALLE eigenen Einträge des aktuellen Filters für den CSV-Export (nicht
 * nur die angezeigte Seite) — `DataTable.csv.fetchAll`. Dasselbe Muster wie
 * `fetchAllTtEntries` der Admin-Analyse: ohne das exportierte der Knopf still
 * die erste Seite, und das fällt erst in Excel auf.
 */
export const TT_MY_ENTRIES_EXPORT_LIMIT = 20_000;

export async function fetchAllMyTimeEntries(
  filters: Omit<MyEntriesFilters, 'page' | 'pageSize'>,
): Promise<{ rows: TimeTrackingEntryResponse[]; isTruncated: boolean }> {
  const rows: TimeTrackingEntryResponse[] = [];
  const pageSize = 500;
  for (let page = 1; ; page += 1) {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('pageSize', String(pageSize));
    if (filters.q) params.set('q', filters.q);
    if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
    if (filters.dateTo) params.set('dateTo', filters.dateTo);
    if (filters.legalEntityId) params.set('legalEntityId', filters.legalEntityId);
    const response = await apiRequest<ListResponse<TimeTrackingEntryResponse>>(
      `/v1/time-tracking/entries?${params.toString()}`,
    );
    rows.push(...response.data);
    const isLastPage = rows.length >= response.meta.total || response.data.length === 0;
    if (isLastPage) return { rows, isTruncated: false };
    if (rows.length >= TT_MY_ENTRIES_EXPORT_LIMIT) return { rows, isTruncated: true };
  }
}

/** Zeiteintrag manuell anlegen (ohne Stoppuhr) — z. B. nachträglich erfasste Stunden. */
export function useCreateTimeEntry() {
  const invalidate = useInvalidateTt();
  return useMutation({
    mutationFn: (input: CreateManualTimeEntryInput) =>
      apiRequest<ItemResponse<TimeTrackingEntryResponse>>('/v1/time-tracking/entries', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateTimeEntry() {
  const invalidate = useInvalidateTt();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTimeTrackingEntryInput }) =>
      apiRequest<ItemResponse<TimeTrackingEntryResponse>>(`/v1/time-tracking/entries/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteTimeEntry() {
  const invalidate = useInvalidateTt();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ data: { id: string } }>(`/v1/time-tracking/entries/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/**
 * Import der Zeiteinträge aus der Alt-App — Schritt 1 (Trockenlauf). Schickt die
 * rohe CSV an den Analyse-Endpunkt; es wird nichts geschrieben (Recht
 * `timetracking.manage`).
 */
export function useAnalyzeTimeEntryImport() {
  return useMutation({
    mutationFn: (csv: string) =>
      apiRequest<ItemResponse<TimeEntryImportAnalysis>>('/v1/time-tracking/import/analyze', {
        method: 'POST',
        body: JSON.stringify({ csv }),
      }),
  });
}

/** Schritt 2 — Import ausführen (idempotent, schreibt in die DB). */
export function useImportTimeEntries() {
  const invalidate = useInvalidateTt();
  return useMutation({
    mutationFn: (input: ImportTimeEntriesInput) =>
      apiRequest<ItemResponse<TimeEntryImportResult>>('/v1/time-tracking/import/execute', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

// ── Stammdaten (Timer-Kaskade Firma → Projekt → Unterkategorie) ──
const MASTER_KEY = ['tt-master'] as const;

/**
 * Gesellschaften (Firmen) für die Zeiterfassung — eigener Endpoint mit
 * `timetracking.read`, damit die Auswahl nicht von `settings.read` abhängt.
 */
export function useTtCompanies() {
  return useQuery({
    queryKey: [...MASTER_KEY, 'companies'],
    queryFn: () => apiRequest<ListResponse<TimerCompany>>('/v1/time-tracking/companies'),
  });
}

/**
 * Hybrid-Projektliste für den Timer: nach Gesellschaft + Kategorie eingeschränkte
 * echte ProERP-Projekte + Zeit-Projekte (§ Umbau Zeiterfassung). Firmen selbst
 * kommen aus `useLegalEntities`.
 */
export function useTimerProjects(filters: { legalEntityId?: string; categoryId?: string; q?: string }) {
  const params = new URLSearchParams();
  if (filters.legalEntityId) params.set('legalEntityId', filters.legalEntityId);
  if (filters.categoryId) params.set('categoryId', filters.categoryId);
  if (filters.q) params.set('q', filters.q);
  const qs = params.toString();
  return useQuery({
    queryKey: [...MASTER_KEY, 'timer-projects', filters],
    queryFn: () => apiRequest<ListResponse<TimerProject>>(`/v1/time-tracking/timer-projects${qs ? `?${qs}` : ''}`),
    enabled: Boolean(filters.legalEntityId && filters.categoryId),
  });
}

/** Zuletzt verwendete Projekte des Benutzers (für das Projekt-Popup). */
export function useRecentProjects(filters: { legalEntityId?: string }) {
  const qs = filters.legalEntityId ? `?legalEntityId=${filters.legalEntityId}` : '';
  return useQuery({
    queryKey: [...MASTER_KEY, 'recent-projects', filters],
    queryFn: () => apiRequest<ListResponse<TimerProject>>(`/v1/time-tracking/recent-projects${qs}`),
    enabled: Boolean(filters.legalEntityId),
  });
}

/** Favoriten-Projekte des Benutzers (Referenzen kind+projectId). */
export function useFavorites() {
  return useQuery({
    queryKey: [...MASTER_KEY, 'favorites'],
    queryFn: () => apiRequest<ListResponse<TimerFavorite>>('/v1/time-tracking/favorites'),
  });
}

export function useAddFavorite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TimerFavorite) =>
      apiRequest<{ data: { ok: true } }>('/v1/time-tracking/favorites', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...MASTER_KEY, 'favorites'] }),
  });
}

export function useRemoveFavorite() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: TimerFavorite) =>
      apiRequest<{ data: { ok: true } }>(`/v1/time-tracking/favorites/${input.kind}/${input.projectId}`, { method: 'DELETE' }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [...MASTER_KEY, 'favorites'] }),
  });
}

/** Zeit-Projekte (Admin-Pflege), optional nach Gesellschaft gefiltert. */
export function useTtProjects(filters: { legalEntityId?: string; q?: string } = {}) {
  const params = new URLSearchParams();
  if (filters.legalEntityId) params.set('legalEntityId', filters.legalEntityId);
  if (filters.q) params.set('q', filters.q);
  const qs = params.toString();
  return useQuery({
    queryKey: [...MASTER_KEY, 'projects', filters],
    queryFn: () => apiRequest<ListResponse<TtProjectResponse>>(`/v1/time-tracking/projects${qs ? `?${qs}` : ''}`),
  });
}

/**
 * Unterkategorien — alle (Admin; Alt-Einträge ohne Kategorie) oder nur die für eine
 * Kategorie freigegebenen (Timer, § Umbau Zeiterfassung). Inaktive kommen mit —
 * Auswahlfelder filtern sie selbst. Die frühere Freigabe am Zeit-Projekt
 * (`?ttProjectId=`) ist seit 2026-09-29 entfernt.
 */
export function useTtSubCategories(opts: { categoryId?: string; enabled?: boolean } = {}) {
  const params = new URLSearchParams();
  if (opts.categoryId) params.set('categoryId', opts.categoryId);
  const qs = params.toString();
  return useQuery({
    queryKey: [...MASTER_KEY, 'sub-categories', opts.categoryId ?? null],
    queryFn: () => apiRequest<ListResponse<TtSubCategoryResponse>>(`/v1/time-tracking/sub-categories${qs ? `?${qs}` : ''}`),
    enabled: opts.enabled ?? true,
  });
}

/** Kategorien (Admin-Pflege) — übergeordnete Gliederungsebene der Timer-Kaskade. */
export function useTtCategories() {
  return useQuery({
    queryKey: [...MASTER_KEY, 'categories'],
    queryFn: () => apiRequest<ListResponse<TtCategoryResponse>>('/v1/time-tracking/categories'),
  });
}

function useInvalidateMaster() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: MASTER_KEY });
}

// Stammdaten-Mutationen (admin-gepflegt, Berechtigung timetracking.manage).
export function useCreateTtSubCategory() {
  const invalidate = useInvalidateMaster();
  return useMutation({
    mutationFn: (input: CreateTtSubCategoryInput) =>
      apiRequest<ItemResponse<TtSubCategoryResponse>>('/v1/time-tracking/sub-categories', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useUpdateTtSubCategory() {
  const invalidate = useInvalidateMaster();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTtSubCategoryInput }) =>
      apiRequest<ItemResponse<TtSubCategoryResponse>>(`/v1/time-tracking/sub-categories/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useDeleteTtSubCategory() {
  const invalidate = useInvalidateMaster();
  return useMutation({
    mutationFn: (id: string) => apiRequest<{ data: { id: string } }>(`/v1/time-tracking/sub-categories/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useCreateTtProject() {
  const invalidate = useInvalidateMaster();
  return useMutation({
    mutationFn: (input: CreateTtProjectInput) =>
      apiRequest<ItemResponse<TtProjectResponse>>('/v1/time-tracking/projects', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useUpdateTtProject() {
  const invalidate = useInvalidateMaster();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTtProjectInput }) =>
      apiRequest<ItemResponse<TtProjectResponse>>(`/v1/time-tracking/projects/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useDeleteTtProject() {
  const invalidate = useInvalidateMaster();
  return useMutation({
    mutationFn: (id: string) => apiRequest<{ data: { id: string } }>(`/v1/time-tracking/projects/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

export function useCreateTtCategory() {
  const invalidate = useInvalidateMaster();
  return useMutation({
    mutationFn: (input: CreateTtCategoryInput) =>
      apiRequest<ItemResponse<TtCategoryResponse>>('/v1/time-tracking/categories', { method: 'POST', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useUpdateTtCategory() {
  const invalidate = useInvalidateMaster();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTtCategoryInput }) =>
      apiRequest<ItemResponse<TtCategoryResponse>>(`/v1/time-tracking/categories/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    onSuccess: invalidate,
  });
}

export function useDeleteTtCategory() {
  const invalidate = useInvalidateMaster();
  return useMutation({
    mutationFn: (id: string) => apiRequest<{ data: { id: string } }>(`/v1/time-tracking/categories/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

// ── Schnellstart: „Zuletzt verwendet" + Favoriten (VB-26027, ADR 0102) ──
// Beides liefert dieselbe startbereite Kombination und geht unverändert an
// `useStartTimer`. Nicht zu verwechseln mit `useFavorites` weiter oben — das
// sind die älteren Favoriten auf der reinen Projekt-Ebene im Auswahl-Popup.

const QUICK_KEY = [...MASTER_KEY, 'quick-start'] as const;

/** Zuletzt aufgezeichnete Kombinationen des Benutzers, jüngste zuerst. */
export function useRecentPresets(limit = 6) {
  return useQuery({
    queryKey: [...QUICK_KEY, 'recent', limit],
    queryFn: () =>
      apiRequest<ListResponse<TimerRecentPreset>>(`/v1/time-tracking/recent-entries?limit=${limit}`),
  });
}

/** Gespeicherte Favoriten des Benutzers, in seiner Reihenfolge. */
export function useFavoriteEntries() {
  return useQuery({
    queryKey: [...QUICK_KEY, 'favorites'],
    queryFn: () => apiRequest<ListResponse<TimerFavoriteEntry>>('/v1/time-tracking/favorite-entries'),
  });
}

/**
 * Nach jeder Änderung an Favoriten ODER am Timer verschiebt sich beides: Der
 * Verlauf bekommt einen neuen ersten Platz, die Favoriten einen neuen Zähler.
 * Deshalb immer den ganzen Schnellstart-Zweig auffrischen.
 */
function useInvalidateQuickStart() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: QUICK_KEY });
}

export function useCreateFavoriteEntry() {
  const invalidate = useInvalidateQuickStart();
  return useMutation({
    mutationFn: (input: CreateTimerFavoriteEntryInput) =>
      apiRequest<ItemResponse<TimerFavoriteEntry>>('/v1/time-tracking/favorite-entries', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

export function useUpdateFavoriteEntry() {
  const invalidate = useInvalidateQuickStart();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTimerFavoriteEntryInput }) =>
      apiRequest<ItemResponse<TimerFavoriteEntry>>(`/v1/time-tracking/favorite-entries/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}

export function useDeleteFavoriteEntry() {
  const invalidate = useInvalidateQuickStart();
  return useMutation({
    mutationFn: (id: string) =>
      apiRequest<{ data: { id: string } }>(`/v1/time-tracking/favorite-entries/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });
}

/** Reihenfolge festlegen — die IDs in der gewünschten Abfolge. */
export function useReorderFavoriteEntries() {
  const invalidate = useInvalidateQuickStart();
  return useMutation({
    mutationFn: (input: ReorderTimerFavoriteEntriesInput) =>
      apiRequest<ListResponse<TimerFavoriteEntry>>('/v1/time-tracking/favorite-entries/order', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: invalidate,
  });
}
